/**
 * Markup de la pizarra `/operaciones.html`.
 *
 * El sprite `fondo.png` ya viene como wood-bg del chrome (Capa 0). El
 * stage es transparente y solo sirve como sistema de coordenadas para
 * posicionar las zonas y el divisor. El título "CURRENT OPERATIONS"
 * va pintado en el PNG; NO se renderiza como elemento.
 */

import type { CampaignSummary, MissionSummary } from '@51esc/shared';

export function renderBoardShell(): string {
  return `
    <div class="board-stage">
      <section class="board-zone board-zone--closed" aria-label="Operaciones cerradas">
        <h2 class="board-zone-label">OPERATIONS CLOSED</h2>
        <div class="board-zone-papers" data-zone="closed"></div>
      </section>

      <div class="board-divider" aria-hidden="true"></div>

      <section class="board-zone board-zone--active" aria-label="Operaciones activas">
        <div class="board-zone-papers" data-zone="active"></div>
      </section>
    </div>
  `;
}

/**
 * Bloque ACCIONES del sidebar derecho del chrome.
 *
 * Solo se monta para usuarios con rol `officer+`. Dos grupos separados
 * por una regla verde: CAMPAÑAS y MISIONES. Cada grupo tiene Nueva,
 * Editar (dropdown) y Borrar (dropdown).
 *
 * Los selects se rellenan en runtime con la lista actual de
 * campañas/misiones; al elegir una en EDITAR navega al editor con
 * `?id=`; al elegir una en BORRAR pide confirmación y borra.
 */
export function renderOperacionesActions(
  campaigns: CampaignSummary[],
  missions: MissionSummary[],
): string {
  return `
    <section class="chrome-actions board-actions">
      <h2 class="chrome-actions-title">ACCIONES</h2>
      <div class="board-actions-group">
        <h3 class="board-actions-subtitle">CAMPAÑAS</h3>
        <ul class="board-actions-list">
          <li>
            <a
              class="board-action-btn"
              href="/operaciones/nueva-campania.html"
              data-board-action="new-campaign"
            >+ NUEVA CAMPAÑA</a>
          </li>
          <li class="board-action-pick">
            <label class="board-pick-label">EDITAR…</label>
            <select
              class="board-pick-select"
              data-board-action="edit-campaign"
              aria-label="Elige campaña a editar"
            >
              <option value="">— elige —</option>
              ${campaigns.map(optCampaign).join('')}
            </select>
          </li>
          <li class="board-action-pick">
            <label class="board-pick-label">BORRAR…</label>
            <select
              class="board-pick-select board-pick-select--danger"
              data-board-action="delete-campaign"
              aria-label="Elige campaña a borrar"
            >
              <option value="">— elige —</option>
              ${campaigns.map(optCampaign).join('')}
            </select>
          </li>
        </ul>
      </div>

      <hr class="board-actions-sep" />

      <div class="board-actions-group">
        <h3 class="board-actions-subtitle">MISIONES</h3>
        <ul class="board-actions-list">
          <li>
            <a
              class="board-action-btn"
              href="/operaciones/nueva-mision.html"
              data-board-action="new-mission"
            >+ NUEVA MISIÓN</a>
          </li>
          <li class="board-action-pick">
            <label class="board-pick-label">EDITAR…</label>
            <select
              class="board-pick-select"
              data-board-action="edit-mission"
              aria-label="Elige misión a editar"
            >
              <option value="">— elige —</option>
              ${missions.map(optMission).join('')}
            </select>
          </li>
          <li class="board-action-pick">
            <label class="board-pick-label">BORRAR…</label>
            <select
              class="board-pick-select board-pick-select--danger"
              data-board-action="delete-mission"
              aria-label="Elige misión a borrar"
            >
              <option value="">— elige —</option>
              ${missions.map(optMission).join('')}
            </select>
          </li>
        </ul>
      </div>
    </section>
  `;
}

function optCampaign(c: CampaignSummary): string {
  const suffix = c.status === 'finished' ? ' (cerrada)' : '';
  return `<option value="${escapeHtml(c.id)}">${escapeHtml(c.name)}${escapeHtml(suffix)}</option>`;
}

function optMission(m: MissionSummary): string {
  const statusLabel =
    m.status === 'flown' ? ' (volada)' : m.status === 'cancelled' ? ' (cancel.)' : '';
  return `<option value="${escapeHtml(m.id)}">${escapeHtml(m.name)}${escapeHtml(statusLabel)}</option>`;
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
